import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {ChangeEvent} from 'react';

import {notifyForError} from 'dev/service-worker-components/errors';
import openEndpointsStore from 'dev/service-worker-components/openEndpointsStore';
import {dbDelete, dbGet, dbPut} from 'dev/service-worker-interop/IndexedDBStore';
import CachedEndpoint, {CachedEndpointWithId} from 'dev/service-worker-interop/stores/CachedEndpoint';
import HelpButton from 'help/HelpButton';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHTER} from 'ui-library/Colors';
import {FloatingInteraction} from 'ui-library/floating-ui/FloatingConstants';

const DEV_ORIGIN = 'https://local.bnch.us:5000';
const ALWAYS_CACHED_ENDPOINTS: Array<{method: 'POST' | 'GET'; url: string}> = [
  {method: 'POST', url: `${DEV_ORIGIN}/1/api/analytics/track`},
];

interface CachedEndpointsTableProps {
  clientId: string;
  endpoints: Array<CachedEndpointWithId>;
  onUpdateEndpointsData: () => void;
}

async function toggleEndpointDisabled(endpointKey: IDBValidKey, disabled: boolean): Promise<void> {
  const endpointData: CachedEndpoint | undefined = await dbGet(await openEndpointsStore(), endpointKey);
  if (endpointData) {
    endpointData.bypassCache = disabled;
    await dbPut(await openEndpointsStore(), endpointKey, endpointData);
  } else {
    notifyForError('Endpoint store was cleared before it could be updated');
  }
}

export default function CachedEndpointsTable(props: CachedEndpointsTableProps): JSX.Element {
  const sortedAndFilteredEndpoints = props.endpoints
    .filter(
      (endpoint) =>
        !ALWAYS_CACHED_ENDPOINTS.some((e) => e.url === endpoint.url && e.method === endpoint.method)
    )
    .sort((e1, e2) => (e1.requestBody ?? '').localeCompare(e2.requestBody ?? ''))
    .sort((e1, e2) => e1.url.localeCompare(e2.url));
  return (
    <div className={css(settingsModalStyles.endpointTableContainer)}>
      <h4 className={css(settingsModalStyles.endpointTableTitle)}>Cached URLs</h4>
      <HelpButton
        title="Caching Strategy"
        renderContent={() =>
          'Endpoints are cached using a hash of the method, URL, and request body. ' +
          'This means that two POST requests with the same path but different request bodies will be ' +
          'cached separately.'
        }
        interaction={FloatingInteraction.HOVER}
      />
      <table>
        <thead>
          <tr>
            <th>Disable</th>
            <th />
            <th className={css(settingsModalStyles.endpointCenteredCell)}>Status</th>
            <th>Endpoint</th>
            <th>Request Data</th>
          </tr>
        </thead>
        <tbody>
          {sortedAndFilteredEndpoints.map((endpoint, key) => {
            const cacheHit = endpoint.cacheHitClientIds.includes(props.clientId);
            return (
              <tr
                key={key}
                className={classNames(css(settingsModalStyles.endpointRow), {
                  [css(settingsModalStyles.endpointCacheHitRow)]: cacheHit,
                })}
              >
                <td className={css(settingsModalStyles.endpointDisableCacheCell)}>
                  <input
                    type="checkbox"
                    checked={endpoint.bypassCache}
                    onChange={async (event: ChangeEvent<HTMLInputElement>) => {
                      const disabled = event.target.checked;
                      await toggleEndpointDisabled(endpoint.id, disabled);
                      props.onUpdateEndpointsData();
                    }}
                  />
                </td>
                <td className={css(settingsModalStyles.endpointCenteredCell)}>
                  <Button
                    text="Clear"
                    buttonStyle={ButtonStyle.TERTIARY_GRAY}
                    size={ButtonSize.SMALL}
                    onClick={async () => {
                      await dbDelete(await openEndpointsStore(), endpoint.id);
                      props.onUpdateEndpointsData();
                    }}
                  />
                </td>
                <td className={css(settingsModalStyles.endpointCenteredCell)}>
                  {endpoint.bypassCache ? 'Disabled' : cacheHit ? 'Cache\u00A0Hit' : 'Cached'}
                </td>
                <td className={css(settingsModalStyles.endpointPathCell)}>
                  <code>{`${endpoint.method} ${endpoint.url.replace(DEV_ORIGIN, '')}`}</code>
                </td>
                <td className={css(settingsModalStyles.endpointRequestDataCell)}>
                  {endpoint.requestBody ? (
                    <pre className={css(settingsModalStyles.endpointRequestData)}>{endpoint.requestBody}</pre>
                  ) : (
                    'N/A'
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <h4>Always Cached Endpoints</h4>
      <ul>
        {ALWAYS_CACHED_ENDPOINTS.map((endpoint, key) => (
          <li key={key}>
            <code>{`${endpoint.method} ${endpoint.url.replace(DEV_ORIGIN, '')}`}</code>
          </li>
        ))}
      </ul>
    </div>
  );
}

export const settingsModalStyles = StyleSheet.create({
  endpointTableTitle: {
    display: 'inline-block',
    paddingRight: 10,
  },
  endpointTableContainer: {
    maxHeight: '75vh',
    overflowY: 'scroll',
  },
  endpointRow: {
    borderColor: GRAY_LIGHTER,
    borderStyle: 'solid',
    borderWidth: 0,
    borderTopWidth: 1,
    borderBottomWidth: 1,
  },
  endpointCacheHitRow: {
    background: 'rgba(30, 228, 207, 0.33)', // 1/3 opacity GREEN_LIGHT
  },
  endpointCenteredCell: {
    paddingRight: 15,
    textAlign: 'center',
  },
  endpointDisableCacheCell: {
    paddingTop: 5,
    paddingBottom: 5,
    paddingLeft: 10,
  },
  endpointPathCell: {
    paddingRight: 15,
  },
  endpointRequestDataCell: {
    paddingTop: 8,
    paddingBottom: 8,
  },
  endpointRequestData: {
    maxHeight: 100,
    overflowY: 'scroll',
    margin: 0,
  },
});
